// Unified Class Editor — Phase B.
//
// One editor. Seven tabs. One Save. One Cancel.
// This is the SINGLE source of truth for editing a class.
//
// Tabs:
//   Basics · Schedule · Registration · Pricing · Instructor · Images · Supplies
//
// Rules honoured (per Phase B brief):
//   - Consolidation, not new capability. Every field mirrors something already
//     writable elsewhere in the app. Reuses MediaManager + InstructorPickerDialog.
//   - Save runs as one coordinated transaction across `classes` and
//     `class_registry`. If any write fails, we roll back the previous write with
//     the pre-save snapshot so partial state is never left behind.
//   - Validation runs before any write. Errors are grouped by tab so the user
//     can jump straight to what needs fixing.
//   - Preview as Parent opens the live public page in a new tab against the
//     current SAVED state. Unsaved edits show a "save first" hint.
//   - Cancel warns if there are unsaved edits.
//
// Legacy-only classes (defined in src/data/classes.ts with no DB row) can only
// have visibility edited from here — content still lives in the static file
// and Phase D will migrate those out. We show that clearly instead of hiding it.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ClassRosterPanel from "./ClassRosterPanel";
import type { ClassSession } from "@/data/classes";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  BookOpen,
  Calendar,
  CheckCircle2,
  Circle,
  DollarSign,
  ExternalLink,
  Image as ImageIcon,
  Info,
  Loader2,
  Mail,
  Package,
  Plus,
  Settings2,
  Trash2,
  User,
  UserPlus,
  Users2,
  History,
} from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { friendlyError } from "@/lib/friendlyError";
import {
  LIFECYCLE_STATUSES,
  STATUS_META,
  upsertRegistryForSlug,
  type RegistryEntry,
  type VisibilityStatus,
} from "@/lib/classRegistry";
import { MediaManager } from "@/components/admin/media/MediaManager";
import { WelcomePacketTab } from "./WelcomePacketTab";
import { resolveClassImage, CLASS_SAFE_COLUMNS } from "@/lib/dbClasses";
import InstructorPickerDialog from "@/components/admin/instructors/InstructorPickerDialog";
import ClassSessionInstructorPanel from "@/components/admin/classes/ClassSessionInstructorPanel";
import ScheduleOptionsSection from "@/components/admin/classes/ScheduleOptionsSection";
import ClassPreviewPane from "@/components/admin/classes/ClassPreviewPane";
import ClassHistoryPanel from "@/components/admin/classes/ClassHistoryPanel";
import FinancialSnapshotPanel from "@/components/admin/classes/FinancialSnapshotPanel";

import type { ClassItem } from "@/data/classes";
import {
  autoFormattedPrice,
  BILLING_UNITS,
  billingUnitLabel,
  coerceBillingUnit,
  displayMatchesAuto,
  publicPriceLabel,
  type BillingUnit,
} from "@/lib/classPricing";
import { describeSemesterForClass, type WeekdayName } from "@/lib/academicCalendar";
import { parseWeekdays } from "@/lib/sessionSchedule";

/* ---------------- Types ---------------- */

export type ClassEditorRow = {
  slug: string;
  title: string;
  dbId?: string; // present iff this is a database-backed class
  /** Create mode: the editor inserts a new class instead of editing one. */
  mode?: "edit" | "create";
  /** Optional pre-fill for create mode (from Duplicate, or blank for New). */
  seed?: Partial<DraftSeed>;
  /** When duplicating, the source's title for the header. */
  sourceLabel?: string;
  /** When duplicating, copy the source's slug into these registration periods. */
  copyRegistrationPeriodsFromSlug?: string;
  legacyPreview?: {
    category: string | null;
    ages: string | null;
    schedule: string | null;
    price: string | null;
    description: string | null;
    thumbnail: string | null;
  };
};

/** Fields the caller can pre-seed for create mode. Mirrors Draft. */
export type DraftSeed = {
  title: string;
  slug: string;
  category: string;
  ages: string;
  short_description: string;
  description: string;
  schedule: string;
  preferred_days: string;
  preferred_time: string;
  class_length: string;
  start_date: string;
  duration: string;
  sessions: SessionRow[];
  capacity: number;
  min_enrollment: number;
  accepts_registrations: boolean;
  calendar_visible: boolean;
  registration_enabled: boolean;
  visibility_status: VisibilityStatus;
  price_per_child: string;
  price_display: string;
  billing_unit: BillingUnit;
  thumbnail_url: string | null;
  supplies_required: SupplyItem[];
  supplies_recommended: SupplyItem[];
  supplies_note: string;
};

type DbClass = {
  id: string;
  slug: string | null;
  title: string;
  description: string;
  short_description: string | null;
  category: string | null;
  ages: string | null;
  capacity: number;
  min_enrollment: number;
  price_per_child: number | null;
  price_display: string | null;
  price_display_omits_unit: boolean | null;
  billing_unit: string | null;
  class_length: string | null;
  preferred_days: string | null;
  preferred_time: string | null;
  schedule: string | null;
  start_date: string | null;
  duration: string | null;
  sessions: SessionRow[] | null;
  supplies: SuppliesShape | null;
  facility_requirements: string | null;
  location: string | null;
  room: string | null;
  thumbnail_url: string | null;
  thumbnail_source: string | null;
  status: "draft" | "coming_soon" | "published" | "archived";
  accepts_registrations: boolean;
  hidden_from_calendar: boolean;
  instructor_id: string | null;
  updated_at: string | null;
  management_type: "silver_sage" | "external_provider" | null;
  provider_name: string | null;
  provider_contact_email: string | null;
  provider_contact_phone: string | null;
  provider_public_contact_text: string | null;
  external_signup_url: string | null;
};


type SessionRow = {
  label: string;
  dates: string;
  price?: number | null;
  full?: boolean;
  /** Closed to NEW registration; existing enrollments are untouched. */
  closed?: boolean;
};

type SupplyItem = { name: string; url: string; required?: boolean };
type SuppliesShape = {
  required?: SupplyItem[];
  recommended?: SupplyItem[];
  note?: string | null;
};

type Draft = {
  // basics
  title: string;
  category: string;
  ages: string;
  short_description: string;
  description: string;
  // schedule
  schedule: string;
  preferred_days: string;
  preferred_time: string;
  class_length: string;
  start_date: string;
  duration: string;
  sessions: SessionRow[];
  // registration
  capacity: number;
  min_enrollment: number;
  accepts_registrations: boolean;
  calendar_visible: boolean;
  registration_enabled: boolean;
  visibility_status: VisibilityStatus;
  // pricing
  price_per_child: string; // kept as string in the form; parsed on save
  price_display: string;
  price_display_omits_unit: boolean;
  billing_unit: BillingUnit;
  // instructor / management
  instructor_id: string | null;
  instructor_label: string | null;
  management_type: "silver_sage" | "external_provider";
  provider_name: string;
  provider_contact_email: string;
  provider_contact_phone: string;
  provider_public_contact_text: string;
  external_signup_url: string;
  // images
  thumbnail_url: string | null;
  // supplies
  supplies_required: SupplyItem[];
  supplies_recommended: SupplyItem[];
  supplies_note: string;
};

type TabKey =
  | "basics"
  | "schedule"
  | "registration"
  | "pricing"
  | "instructor"
  | "images"
  | "supplies"
  | "welcome_packet"
  | "history"
  | "advanced";

const TABS: Array<{ key: TabKey; label: string; Icon: typeof BookOpen }> = [
  { key: "basics", label: "Overview", Icon: BookOpen },
  { key: "schedule", label: "Schedule", Icon: Calendar },
  { key: "registration", label: "Registration", Icon: Users2 },
  { key: "instructor", label: "Instructor", Icon: User },
  { key: "pricing", label: "Pricing", Icon: DollarSign },
  { key: "images", label: "Images", Icon: ImageIcon },
  { key: "supplies", label: "Supplies & Details", Icon: Package },
  { key: "welcome_packet", label: "Welcome Packet", Icon: Mail },
  { key: "history", label: "History", Icon: History },
  { key: "advanced", label: "Advanced", Icon: Settings2 },
];

/* ---------------- Validation ----------------
 *
 * Rules:
 * - Limits are sized for real-world use. Short structured fields keep tight
 *   caps; long-form/notes fields get generous caps.
 * - Max-length checks are *dirty-field aware*. An unchanged legacy value that
 *   predates the current limit does NOT block saving unrelated edits — it is
 *   surfaced as a warning on that specific field the next time the admin
 *   actually touches it. This prevents "String must contain at most 100
 *   character(s)" from trapping an ages-only save. */

const supplySchema = z.object({
  name: z.string().trim().min(1, "Item name required").max(200),
  url: z.string().trim().max(1000).optional().or(z.literal("")),
});

// Per-field human labels + tab mapping, used to build precise error messages.
const FIELD_META: Record<string, { label: string; tab: TabKey; max?: number }> = {
  title: { label: "Title", tab: "basics", max: 200 },
  category: { label: "Category", tab: "basics", max: 200 },
  ages: { label: "Ages", tab: "basics", max: 200 },
  short_description: { label: "Short description", tab: "basics", max: 500 },
  description: { label: "Description", tab: "basics", max: 10000 },
  schedule: { label: "Schedule summary", tab: "schedule", max: 500 },
  preferred_days: { label: "Preferred days", tab: "schedule", max: 200 },
  preferred_time: { label: "Preferred time", tab: "schedule", max: 200 },
  class_length: { label: "Class length", tab: "schedule", max: 200 },
  start_date: { label: "Start date (display)", tab: "schedule", max: 500 },
  duration: { label: "Duration", tab: "schedule", max: 200 },
  sessions: { label: "Sessions", tab: "schedule" },
  capacity: { label: "Capacity", tab: "registration" },
  min_enrollment: { label: "Minimum enrollment", tab: "registration" },
  price_per_child: { label: "Price", tab: "pricing" },
  price_display: { label: "Price display", tab: "pricing", max: 200 },
  billing_unit: { label: "Billing unit", tab: "pricing" },
  instructor_id: { label: "Instructor", tab: "instructor" },
  supplies_required: { label: "Required supplies", tab: "supplies" },
  supplies_recommended: { label: "Recommended supplies", tab: "supplies" },
  supplies_note: { label: "Supplies note", tab: "supplies", max: 10000 },
};

const draftSchema = z.object({
  title: z.string().trim().min(1, "Title is required").max(FIELD_META.title.max!),
  category: z.string().trim().max(FIELD_META.category.max!),
  ages: z.string().trim().max(FIELD_META.ages.max!),
  short_description: z.string().trim().max(FIELD_META.short_description.max!),
  description: z.string().trim().min(1, "Description is required").max(FIELD_META.description.max!),

  schedule: z.string().trim().max(FIELD_META.schedule.max!),
  preferred_days: z.string().trim().max(FIELD_META.preferred_days.max!),
  preferred_time: z.string().trim().max(FIELD_META.preferred_time.max!),
  class_length: z.string().trim().max(FIELD_META.class_length.max!),
  start_date: z.string().trim().max(FIELD_META.start_date.max!),
  duration: z.string().trim().max(FIELD_META.duration.max!),
  sessions: z
    .array(
      z.object({
        label: z.string().trim().min(1, "Session label required").max(200),
        dates: z.string().trim().min(1, "Session dates required").max(500),
        price: z
          .union([z.number().nonnegative(), z.null(), z.undefined()])
          .optional(),
        full: z.boolean().optional(),
        /** Closed to NEW registration; existing enrollments are untouched. */
        closed: z.boolean().optional(),
      }),
    )
    .max(48, "48 sessions maximum"),

  capacity: z.number().int().min(1, "Capacity must be at least 1").max(500),
  min_enrollment: z.number().int().min(0).max(500),

  price_per_child: z
    .string()
    .trim()
    .refine((v) => v === "" || !Number.isNaN(Number(v)), "Price must be a number")
    .refine((v) => v === "" || Number(v) >= 0, "Price cannot be negative"),
  price_display: z.string().trim().max(FIELD_META.price_display.max!),
  billing_unit: z.enum(["per_session", "per_month", "per_semester"]),

  instructor_id: z.string().uuid().nullable(),

  supplies_required: z.array(supplySchema).max(100),
  supplies_recommended: z.array(supplySchema).max(100),
  supplies_note: z.string().trim().max(FIELD_META.supplies_note.max!),
});

type ValidationErrors = Partial<Record<TabKey, string[]>>;

/**
 * Was this field actually changed by the admin in this edit session?
 * Used to skip too-long errors on untouched legacy values.
 */
function fieldChanged(draft: Draft, initial: Draft | null, field: string): boolean {
  if (!initial) return true;
  const a = (draft as any)[field];
  const b = (initial as any)[field];
  return JSON.stringify(a) !== JSON.stringify(b);
}

function validate(
  draft: Draft,
  initial: Draft | null,
): { ok: true } | { ok: false; errors: ValidationErrors } {
  const result = draftSchema.safeParse(draft);
  if (result.success) {
    if (draft.min_enrollment > draft.capacity) {
      return {
        ok: false,
        errors: { registration: ["Minimum enrollment can't exceed capacity."] },
      };
    }
    return { ok: true };
  }
  const errors: ValidationErrors = {};
  const push = (tab: TabKey, msg: string) => {
    (errors[tab] ??= []).push(msg);
  };
  for (const issue of result.error.issues) {
    const field = String(issue.path[0] ?? "");
    const meta = FIELD_META[field] ?? { label: field || "Field", tab: "basics" as TabKey };

    // Dirty-field-aware: skip too-long errors for untouched legacy values so
    // one long legacy string can't block an unrelated edit like Ages.
    const isTooBig = issue.code === "too_big";
    if (isTooBig && !fieldChanged(draft, initial, field)) continue;

    let msg = issue.message;
    if (isTooBig && typeof (draft as any)[field] === "string") {
      const value = (draft as any)[field] as string;
      const limit = meta.max ?? (issue as any).maximum;
      msg = `${meta.label} is too long. It has ${value.length} characters; the limit is ${limit}. Shorten it${
        field === "start_date" ? " or let the structured sessions generate the dates automatically" : ""
      }.`;
    } else if (issue.message === issue.message && !msg.toLowerCase().includes(meta.label.toLowerCase())) {
      msg = `${meta.label}: ${msg}`;
    }
    push(meta.tab, msg);
  }
  return { ok: false, errors };
}

/* ---------------- Helpers ---------------- */

function makeInitialDraft(dbRow: DbClass | null, registryEntry: RegistryEntry | null, fallbackTitle: string): Draft {
  const s = dbRow?.sessions ?? [];
  return {
    title: dbRow?.title ?? fallbackTitle ?? "",
    category: dbRow?.category ?? "",
    ages: dbRow?.ages ?? "",
    short_description: dbRow?.short_description ?? "",
    description: dbRow?.description ?? "",
    schedule: dbRow?.schedule ?? "",
    preferred_days: dbRow?.preferred_days ?? "",
    preferred_time: dbRow?.preferred_time ?? "",
    class_length: dbRow?.class_length ?? "",
    start_date: dbRow?.start_date ?? "",
    duration: dbRow?.duration ?? "",
    sessions: Array.isArray(s) ? s.map((x) => ({ ...x })) : [],
    capacity: dbRow?.capacity ?? 10,
    min_enrollment: dbRow?.min_enrollment ?? 5,
    accepts_registrations: dbRow?.accepts_registrations ?? false,
    calendar_visible: registryEntry?.calendar_visible ?? true,
    registration_enabled: registryEntry?.registration_enabled ?? true,
    visibility_status: (registryEntry?.visibility_status ?? "published") as VisibilityStatus,
    price_per_child: dbRow?.price_per_child == null ? "" : String(dbRow.price_per_child),
    price_display: dbRow?.price_display ?? "",
    price_display_omits_unit: !!dbRow?.price_display_omits_unit,
    billing_unit: coerceBillingUnit(dbRow?.billing_unit),
    instructor_id: dbRow?.instructor_id ?? null,
    instructor_label: null,
    management_type: (dbRow?.management_type ?? "silver_sage") as "silver_sage" | "external_provider",
    provider_name: dbRow?.provider_name ?? "",
    provider_contact_email: dbRow?.provider_contact_email ?? "",
    provider_contact_phone: dbRow?.provider_contact_phone ?? "",
    provider_public_contact_text: dbRow?.provider_public_contact_text ?? "",
    external_signup_url: dbRow?.external_signup_url ?? "",
    thumbnail_url: dbRow?.thumbnail_url ?? null,
    supplies_required: dbRow?.supplies?.required ?? [],
    supplies_recommended: dbRow?.supplies?.recommended ?? [],
    supplies_note: dbRow?.supplies?.note ?? "",
  };
}

function isEqual<T>(a: T, b: T): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

/* ---------------- Publish readiness + live preview mapping ---------------- */

interface ReadinessCheck {
  key: string;
  label: string;
  section: TabKey;
  ok: boolean;
}

function evaluateReadiness(draft: Draft, slug?: string, liveAssignmentCount = 0): ReadinessCheck[] {
  const isExternal = draft.management_type === "external_provider";
  const isInstructorWanted = draft.visibility_status === "instructor_wanted";
  const hasSchedule =
    (draft.sessions?.length ?? 0) > 0 || draft.schedule.trim().length > 0;
  const hasPrice =
    draft.price_per_child.trim() !== "" || draft.price_display.trim() !== "";
  // A class has a cover image if the canonical resolver returns one — either
  // the DB thumbnail_url OR a migrated legacy bundled asset mapped by slug.
  // Public catalog, class page, and preview all use this same resolver, so
  // the validator must agree with them.
  const hasCoverImage = !!resolveClassImage(slug, draft.thumbnail_url);
  return [
    { key: "title", label: "Give the class a title", section: "basics", ok: draft.title.trim().length > 0 },
    {
      key: "description",
      label: "Add a description families will read",
      section: "basics",
      ok: draft.description.trim().length >= 20,
    },
    { key: "category", label: "Choose a category", section: "basics", ok: draft.category.trim().length > 0 },
    { key: "ages", label: "Set the age range", section: "basics", ok: draft.ages.trim().length > 0 },
    { key: "image", label: "Add a cover image", section: "images", ok: hasCoverImage },
    { key: "schedule", label: "Add at least one session or a schedule summary", section: "schedule", ok: hasSchedule },
    { key: "capacity", label: "Set how many students can enroll", section: "registration", ok: draft.capacity > 0 },
    { key: "pricing", label: "Set the price (or a display like \"Free\")", section: "pricing", ok: hasPrice },
    isExternal
      ? {
          key: "external_url",
          label: "Add the provider's registration link",
          section: "instructor",
          ok: draft.external_signup_url.trim().length > 0,
        }
      : {
          key: "instructor",
          label: isInstructorWanted
            ? "Instructor Wanted — no assignment required"
            : "Assign a session instructor",
          section: "instructor",
          ok: isInstructorWanted || liveAssignmentCount > 0,
        },
  ];
}

const PREVIEW_PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'><rect width='400' height='300' fill='#f5f0e6'/><text x='50%' y='55%' text-anchor='middle' font-family='Georgia, serif' font-size='18' fill='#5f7a4b' font-style='italic'>Silver Sage</text></svg>`,
  );

function mapDraftVisibilityToPublicStatus(draft: Draft): ClassItem["status"] {
  if (draft.management_type === "external_provider") return "open";
  switch (draft.visibility_status) {
    case "coming_soon":
      return "coming-soon";
    case "instructor_wanted":
      return "instructor-wanted";
    case "waitlist":
      return "waitlist";
    case "published":
      return draft.accepts_registrations ? "open" : "coming-soon";
    case "draft":
    case "hidden":
    case "archived":
    default:
      return "coming-soon";
  }
}

function draftToClassItem(
  draft: Draft,
  slug: string,
  dbId?: string,
  liveAssignmentCount = 0,
): ClassItem {
  const isExternal = draft.management_type === "external_provider";
  const priceNumber = Number(draft.price_per_child);
  const hasInstructor = isExternal ? true : liveAssignmentCount > 0;
  // Public label uses the shared resolver so Live Parent Preview and the
  // published card can never diverge.
  const price = publicPriceLabel({
    pricePerChild: Number.isFinite(priceNumber) ? priceNumber : 0,
    billingUnit: draft.billing_unit,
    priceDisplay: draft.price_display,
    externalPayment: isExternal,
    hasInstructor,
  });
  return {
    id: dbId ? `db:${slug}` : slug || "preview",
    title: draft.title.trim() || "Untitled class",
    description: draft.description || "",
    shortDescription: draft.short_description || undefined,
    ages: draft.ages || "—",
    schedule: draft.schedule || "",
    duration: draft.duration || draft.class_length || "",
    price,
    unitPrice: Number(draft.price_per_child) || 0,
    startDate: draft.start_date || "TBA",
    status: mapDraftVisibilityToPublicStatus(draft),
    image: resolveClassImage(slug, draft.thumbnail_url) || PREVIEW_PLACEHOLDER,
    category: draft.category || "Class",
    sessions: draft.sessions?.length ? draft.sessions : undefined,
    supplyList:
      draft.supplies_required.length || draft.supplies_recommended.length || draft.supplies_note
        ? {
            required: draft.supplies_required,
            recommended: draft.supplies_recommended,
            note: draft.supplies_note || undefined,
          }
        : undefined,
    externalSignupUrl: draft.external_signup_url || undefined,
    billingUnit: draft.billing_unit,
    managementType: draft.management_type,
    providerName: draft.provider_name || undefined,
    providerContactEmail: draft.provider_contact_email || undefined,
    providerContactPhone: draft.provider_contact_phone || undefined,
    providerPublicContactText: draft.provider_public_contact_text || undefined,
    dbId,
    hasInstructor,
    sessionLengthWeeks: (() => {
      const m = (draft.duration || draft.class_length || "").match(/(\d+)\s*week/i);
      const n = m ? parseInt(m[1], 10) : NaN;
      return Number.isFinite(n) && n > 0 ? n : 1;
    })(),
  };
}

/* ---------------- Component ---------------- */



export function ClassEditorDialog({
  row,
  open,
  onClose,
  onSaved,
  onCreated,
}: {
  row: ClassEditorRow | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** Called after a successful create — parent should reopen in edit mode. */
  onCreated?: (dbId: string, slug: string) => void;
}) {
  const mode: "edit" | "create" = row?.mode === "create" ? "create" : "edit";
  const [tab, setTab] = useState<TabKey>("basics");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dbRow, setDbRow] = useState<DbClass | null>(null);
  const [registryEntry, setRegistryEntry] = useState<RegistryEntry | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const initialRef = useRef<Draft | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [showPicker, setShowPicker] = useState(false);
  // Canonical staffing signal: how many LIVE instructor_class_assignments rows
  // cover this class's current/upcoming session. Never derived from
  // classes.instructor_id.
  const [liveAssignmentCount, setLiveAssignmentCount] = useState(0);
  const [showLeaveWarn, setShowLeaveWarn] = useState(false);
  // create-mode slug (separate — not part of Draft's dirty diff on edit).
  const [slugInput, setSlugInput] = useState("");
  const slugTouchedRef = useRef(false);

  const isDb = !!row?.dbId;
  // Phase D complete — every class is DB-backed, so the editor is always full.
  const isFullEditor = true;

  // Welcome packet lives in its own table; the tab hands us a save function so
  // the dialog's single Save also persists it.
  const packetSaveRef = useRef<(() => Promise<void>) | null>(null);
  const [packetDirty, setPacketDirty] = useState(false);
  const registerPacketSave = useCallback((fn: (() => Promise<void>) | null) => {
    packetSaveRef.current = fn;
  }, []);

  const draftDirty = useMemo(() => {
    if (!draft || !initialRef.current) return false;
    return !isEqual(draft, initialRef.current);
  }, [draft]);
  const dirty = draftDirty || packetDirty;


  /* --- Load DB row + registry entry --- */
  useEffect(() => {
    if (!open || !row) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrors({});
      setTab("basics");
      setPacketDirty(false);
      slugTouchedRef.current = false;

      // ---- Create mode: no DB fetch, seed from provided values ----
      if (mode === "create") {
        const seed = row.seed ?? {};
        const d = makeInitialDraft(null, null, row.title);
        Object.assign(d, seed);
        // Enforce create-mode defaults
        d.visibility_status = (seed.visibility_status ?? "draft") as VisibilityStatus;
        d.accepts_registrations = seed.accepts_registrations ?? false;
        d.registration_enabled = seed.registration_enabled ?? true;
        d.calendar_visible = seed.calendar_visible ?? true;
        d.instructor_id = null;
        d.instructor_label = null;
        if (cancelled) return;
        setDbRow(null);
        setRegistryEntry(null);
        setDraft(d);
        initialRef.current = JSON.parse(JSON.stringify(d)) as Draft;
        setSlugInput(seed.slug ?? row.slug ?? slugify(d.title));
        setLoading(false);
        return;
      }

      // ---- Edit mode ----
      let db: DbClass | null = null;
      if (row.dbId) {
        const { data, error } = await (supabase as any)
          .from("classes")
          .select(CLASS_SAFE_COLUMNS)
          .eq("id", row.dbId)
          .maybeSingle();
        if (error) friendlyError({ what: "I couldn't load that class.", technical: error });
        db = (data as DbClass) ?? null;
        // Internal provider contact details are admin-only (revoked from public reads).
        if (db) {
          const { data: contact } = await (supabase as any).rpc("admin_class_provider_contact", {
            _class_id: row.dbId,
          });
          const c = Array.isArray(contact) ? contact[0] : contact;
          if (c) {
            db.provider_contact_email = c.provider_contact_email ?? null;
            db.provider_contact_phone = c.provider_contact_phone ?? null;
          }
        }
      }


      const { data: regData } = await (supabase as any)
        .from("class_registry")
        .select(
          "id, slug, visibility_status, registration_enabled, calendar_visible, archived_at, linked_class_id, externally_managed, created_at, updated_at",
        )
        .eq("slug", row.slug)
        .maybeSingle();

      if (cancelled) return;
      setDbRow(db);
      setRegistryEntry((regData as RegistryEntry) ?? null);
      const d = makeInitialDraft(db, (regData as RegistryEntry) ?? null, row.title);
      // Legacy-only: prefill visible copy from static preview so the editor is honest.
      if (!db && row.legacyPreview) {
        d.category = row.legacyPreview.category ?? "";
        d.ages = row.legacyPreview.ages ?? "";
        d.schedule = row.legacyPreview.schedule ?? "";
        d.description = row.legacyPreview.description ?? "";
        d.price_display = row.legacyPreview.price ?? "";
        d.thumbnail_url = row.legacyPreview.thumbnail ?? null;
      }
      setDraft(d);
      initialRef.current = JSON.parse(JSON.stringify(d)) as Draft;
      setSlugInput(row.slug);

      // Fetch instructor label so the picker shows something friendly.
      if (db?.instructor_id) {
        const { data: inst } = await (supabase as any)
          .from("instructors")
          .select("id,full_name,email")
          .eq("id", db.instructor_id)
          .maybeSingle();
        if (!cancelled && inst) {
          setDraft((prev) => (prev ? { ...prev, instructor_label: `${inst.full_name}${inst.email ? ` · ${inst.email}` : ""}` } : prev));
          initialRef.current = initialRef.current
            ? { ...initialRef.current, instructor_label: `${inst.full_name}${inst.email ? ` · ${inst.email}` : ""}` }
            : initialRef.current;
        }
      }

      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, row?.slug, row?.dbId, mode]);

  const patch = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  /* --- Warn on browser refresh / tab close while dirty --- */
  useEffect(() => {
    if (!open || !dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore custom text but require returnValue set to trigger.
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [open, dirty]);


  /* --- Save: one coordinated transaction --- */
  const save = async () => {
    if (!draft || !row) return;

    const check = validate(draft, initialRef.current);
    if (check.ok === false) {
      const errs = check.errors;
      setErrors(errs);
      const firstBadTab = TABS.find((t) => errs[t.key]?.length)?.key;
      if (firstBadTab) setTab(firstBadTab);
      toast.error("Please fix the highlighted fields before saving.");
      return;
    }
    setErrors({});

    // Welcome packet lives in its own table. Persist it FIRST, so a failure or
    // early return anywhere in the class/registry writes can never discard it.
    if (mode !== "create" && packetSaveRef.current) {
      try {
        await packetSaveRef.current();
        setPacketDirty(false);
      } catch (e) {
        friendlyError({ what: "I couldn't save the welcome packet.", technical: e });
        return;
      }
    }



    // ============ CREATE MODE ============
    if (mode === "create") {
      const slug = slugify(slugInput || draft.title);
      if (!slug) {
        setErrors({ basics: ["Slug is required."] });
        setTab("basics");
        toast.error("Slug is required.");
        return;
      }
      setSaving(true);
      let insertedId: string | null = null;
      try {
        // Uniqueness pre-check (belt) — insert conflict is the suspenders.
        const { data: existing, error: exErr } = await (supabase as any)
          .from("classes")
          .select("id")
          .eq("slug", slug)
          .maybeSingle();
        if (exErr) throw exErr;
        if (existing?.id) {
          setErrors({ basics: [`Slug "${slug}" is already taken. Choose another.`] });
          setTab("basics");
          setSaving(false);
          return;
        }
        const { data: regExisting } = await (supabase as any)
          .from("class_registry")
          .select("slug,linked_class_id")
          .eq("slug", slug)
          .maybeSingle();
        if (regExisting?.linked_class_id) {
          setErrors({ basics: [`Slug "${slug}" is already registered. Choose another.`] });
          setTab("basics");
          setSaving(false);
          return;
        }

        const supplies: SuppliesShape = {
          required: draft.supplies_required,
          recommended: draft.supplies_recommended,
          note: draft.supplies_note || null,
        };
        const insertRow: Record<string, unknown> = {
          slug,
          title: draft.title.trim(),
          category: draft.category.trim() || null,
          ages: draft.ages.trim() || null,
          short_description: draft.short_description.trim() || null,
          description: draft.description.trim(),
          schedule: draft.schedule.trim() || null,
          preferred_days: draft.preferred_days.trim() || null,
          preferred_time: draft.preferred_time.trim() || null,
          class_length: draft.class_length.trim() || null,
          start_date: draft.start_date.trim() || null,
          duration: draft.duration.trim() || null,
          sessions: draft.sessions,
          capacity: draft.capacity,
          min_enrollment: draft.min_enrollment,
          accepts_registrations: false, // never auto-publish
          price_per_child: draft.price_per_child.trim() === "" ? null : Number(draft.price_per_child),
          price_display: draft.price_display.trim() || null,
          price_display_omits_unit: draft.price_display.trim() ? draft.price_display_omits_unit : false,
          billing_unit: draft.billing_unit,
          instructor_id: null, // never carry an instructor into a new class
          thumbnail_url: draft.thumbnail_url,
          supplies,
          status: "draft",
          hidden: false,
          management_type: draft.management_type,
          provider_name: draft.provider_name.trim() || null,
          provider_contact_email: draft.provider_contact_email.trim() || null,
          provider_contact_phone: draft.provider_contact_phone.trim() || null,
          provider_public_contact_text: draft.provider_public_contact_text.trim() || null,
          external_signup_url: draft.external_signup_url.trim() || null,
        };

        const { data: inserted, error: insErr } = await (supabase as any)
          .from("classes")
          .insert(insertRow)
          .select("id,slug")
          .single();
        if (insErr) throw insErr;
        insertedId = inserted.id as string;

        // Registry: autoseed trigger created a row on insert. Apply chosen visibility.
        const { error: regErr } = await upsertRegistryForSlug(slug, {
          visibility_status: draft.visibility_status,
          calendar_visible: draft.calendar_visible,
          registration_enabled: draft.registration_enabled,
        });
        if (regErr) {
          // Roll back the inserted class.
          if (insertedId) {
            await (supabase as any).from("classes").delete().eq("id", insertedId);
            await (supabase as any).from("class_registry").delete().eq("slug", slug);
          }
          throw regErr;
        }

        // Optional: copy registration-period membership from a source slug.
        if (row.copyRegistrationPeriodsFromSlug) {
          const { data: periods } = await (supabase as any)
            .from("registration_periods")
            .select("id,class_ids")
            .contains("class_ids", [row.copyRegistrationPeriodsFromSlug]);
          for (const p of (periods ?? []) as Array<{ id: string; class_ids: string[] }>) {
            const next = Array.from(new Set([...(p.class_ids ?? []), slug]));
            await (supabase as any)
              .from("registration_periods")
              .update({ class_ids: next })
              .eq("id", p.id);
          }
        }

        // Read back both records to verify.
        const [{ data: verifyClass }, { data: verifyReg }] = await Promise.all([
          (supabase as any).from("classes").select("id,slug").eq("id", insertedId).maybeSingle(),
          (supabase as any).from("class_registry").select("slug").eq("slug", slug).maybeSingle(),
        ]);
        if (!verifyClass?.id || !verifyReg?.slug) {
          toast.error("Class was created but verification failed. Reopen it from the list.");
          // Don't roll back — the records exist; just surface & keep editor open.
          onSaved();
          setSaving(false);
          return;
        }

        toast.success("Class created as Draft");
        initialRef.current = JSON.parse(JSON.stringify(draft)) as Draft;
        onSaved();
        onCreated?.(insertedId, slug);
        onClose();
      } catch (e) {
        friendlyError({ what: "I couldn't create that class.", technical: e });
      } finally {
        setSaving(false);
      }
      return;
    }

    // ============ EDIT MODE ============
    setSaving(true);

    // Snapshot for rollback if the second write fails.
    const classesSnapshot = dbRow ? { ...dbRow } : null;
    const registrySnapshot = registryEntry ? { ...registryEntry } : null;

    try {
      let wroteClasses = false;
      // Optimistic lock: refuse to overwrite a newer edit from another tab/user.
      if (isDb && row.dbId && dbRow?.updated_at) {
        const { data: fresh, error: freshErr } = await (supabase as any)
          .from("classes")
          .select("updated_at")
          .eq("id", row.dbId)
          .maybeSingle();
        if (freshErr) throw freshErr;
        if (fresh?.updated_at && fresh.updated_at !== dbRow.updated_at) {
          setSaving(false);
          toast.error(
            "Someone else edited this class since you opened it. Reload to see their changes before saving — your edits are still in the form.",
            { duration: 8000 },
          );
          return;
        }
      }

      if (isDb && row.dbId) {

        const supplies: SuppliesShape = {
          required: draft.supplies_required,
          recommended: draft.supplies_recommended,
          note: draft.supplies_note || null,
        };
        const patchRow: Record<string, unknown> = {
          title: draft.title.trim(),
          category: draft.category.trim() || null,
          ages: draft.ages.trim() || null,
          short_description: draft.short_description.trim() || null,
          description: draft.description.trim(),
          schedule: draft.schedule.trim() || null,
          preferred_days: draft.preferred_days.trim() || null,
          preferred_time: draft.preferred_time.trim() || null,
          class_length: draft.class_length.trim() || null,
          start_date: draft.start_date.trim() || null,
          duration: draft.duration.trim() || null,
          sessions: draft.sessions,
          capacity: draft.capacity,
          min_enrollment: draft.min_enrollment,
          accepts_registrations: draft.accepts_registrations,
          price_per_child: draft.price_per_child.trim() === "" ? null : Number(draft.price_per_child),
          price_display: draft.price_display.trim() || null,
          price_display_omits_unit: draft.price_display.trim() ? draft.price_display_omits_unit : false,
          billing_unit: draft.billing_unit,
          // classes.instructor_id is legacy administrative metadata and is NOT
          // written by the editor. Staffing lives in instructor_class_assignments.
          thumbnail_url: draft.thumbnail_url,
          supplies,
          management_type: draft.management_type,
          provider_name: draft.provider_name.trim() || null,
          provider_contact_email: draft.provider_contact_email.trim() || null,
          provider_contact_phone: draft.provider_contact_phone.trim() || null,
          provider_public_contact_text: draft.provider_public_contact_text.trim() || null,
          external_signup_url: draft.external_signup_url.trim() || null,
        };
        // Skip write when nothing in the classes-owned portion changed. Registry
        // fields (visibility_status, calendar_visible, registration_enabled) and
        // instructor_label are handled separately, so we exclude them here.
        const classesChanged =
          !dbRow ||
          Object.entries(patchRow).some(([k, v]) => {
            const prev = (dbRow as Record<string, unknown>)[k];
            return JSON.stringify(prev ?? null) !== JSON.stringify(v ?? null);
          });
        if (classesChanged) {
          const { error: classErr } = await (supabase as any)
            .from("classes")
            .update(patchRow)
            .eq("id", row.dbId);
          if (classErr) throw classErr;
          wroteClasses = true;
        }
      }
      void wroteClasses;


      // Registry write (visibility, calendar, registration flag).
      const registryChanged =
        !registryEntry ||
        registryEntry.visibility_status !== draft.visibility_status ||
        (registryEntry.calendar_visible ?? true) !== draft.calendar_visible ||
        (registryEntry.registration_enabled ?? true) !== draft.registration_enabled;

      if (registryChanged) {
        const { error: regErr } = await upsertRegistryForSlug(row.slug, {
          visibility_status: draft.visibility_status,
          calendar_visible: draft.calendar_visible,
          registration_enabled: draft.registration_enabled,
        });
        if (regErr) {
          // Roll back the classes update.
          if (isDb && row.dbId && classesSnapshot) {
            await (supabase as any)
              .from("classes")
              .update({
                title: classesSnapshot.title,
                category: classesSnapshot.category,
                ages: classesSnapshot.ages,
                short_description: classesSnapshot.short_description,
                description: classesSnapshot.description,
                schedule: classesSnapshot.schedule,
                preferred_days: classesSnapshot.preferred_days,
                preferred_time: classesSnapshot.preferred_time,
                class_length: classesSnapshot.class_length,
                start_date: classesSnapshot.start_date,
                duration: classesSnapshot.duration,
                sessions: classesSnapshot.sessions,
                capacity: classesSnapshot.capacity,
                min_enrollment: classesSnapshot.min_enrollment,
                accepts_registrations: classesSnapshot.accepts_registrations,
                price_per_child: classesSnapshot.price_per_child,
                price_display: classesSnapshot.price_display,
                price_display_omits_unit: classesSnapshot.price_display_omits_unit ?? false,
                billing_unit: classesSnapshot.billing_unit,
                instructor_id: classesSnapshot.instructor_id,
                thumbnail_url: classesSnapshot.thumbnail_url,
                supplies: classesSnapshot.supplies,
              })
              .eq("id", row.dbId);
          }
          void registrySnapshot; // snapshot kept for future symmetry
          throw regErr;
        }
      }

      // (Welcome packet was already persisted before the class writes above.)


      toast.success("Class saved");
      // Refresh initial to current so dirty check resets.
      initialRef.current = JSON.parse(JSON.stringify(draft)) as Draft;

      onSaved();
      onClose();
    } catch (e) {
      friendlyError({ what: "I couldn't save that class.", technical: e });
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (dirty) {
      setShowLeaveWarn(true);
      return;
    }
    onClose();
  };

  /* --- Image save is its own atomic write (uploads storage + writes column). --- */
  const saveImage = async (file: File) => {
    if (!row?.dbId) {
      toast.error("Add this class to the database before uploading images.");
      return;
    }
    const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${row.dbId}/${Date.now()}-${safe}`;

    // Step 1 — upload the new object. The currently saved image is untouched
    // until every later step succeeds.
    const { error: upErr } = await supabase.storage
      .from("class-thumbnails")
      .upload(path, file, { upsert: false, contentType: file.type });
    if (upErr) throw Object.assign(upErr, { __operation: "Upload image to storage (class-thumbnails)" });

    // Any failure past this point must not leave an orphaned storage object.
    const cleanupOrphan = async () => {
      try {
        await supabase.storage.from("class-thumbnails").remove([path]);
      } catch {
        /* best effort */
      }
    };

    try {
      const { data: signed, error: signErr } = await supabase.storage
        .from("class-thumbnails")
        .createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
      if (signErr || !signed?.signedUrl) {
        throw Object.assign(signErr ?? new Error("Could not sign URL"), {
          __operation: "Create signed URL for uploaded image",
        });
      }
      const url = signed.signedUrl;

      const { error: dbErr } = await (supabase as any)
        .from("classes")
        .update({ thumbnail_url: url, thumbnail_source: "upload" })
        .eq("id", row.dbId);
      if (dbErr) throw Object.assign(dbErr, { __operation: "Save image URL on the class record" });

      const { data: verify, error: verifyErr } = await (supabase as any)
        .from("classes")
        .select("thumbnail_url")
        .eq("id", row.dbId)
        .maybeSingle();
      if (verifyErr) throw Object.assign(verifyErr, { __operation: "Read back saved image URL" });
      if (!verify || verify.thumbnail_url !== url) {
        throw Object.assign(new Error("Saved image did not verify on read-back."), {
          __operation: "Verify saved image URL",
        });
      }

      patch({ thumbnail_url: url });
      initialRef.current = initialRef.current ? { ...initialRef.current, thumbnail_url: url } : initialRef.current;
      setDbRow((r) => (r ? { ...r, thumbnail_url: url } : r));
      toast.success("Image saved");
      onSaved();
    } catch (e) {
      await cleanupOrphan();
      throw e;
    }
  };

  const previewAsParent = () => {
    if (!row) return;
    if (dirty) {
      toast.message("Showing the currently saved version. Save first to preview your edits.");
    }
    window.open(`/classes?open=${encodeURIComponent(row.slug)}`, "_blank", "noopener");
  };

  if (!row) return null;

  // Live-preview ClassItem derived from the current draft. Recomputed on every
  // keystroke so parents' view updates before Save.
  const previewItem: ClassItem | null = draft
    ? draftToClassItem(draft, row.slug || slugInput, row.dbId, liveAssignmentCount)
    : null;
  const readiness = draft
    ? evaluateReadiness(draft, row.slug || slugInput, liveAssignmentCount)
    : [];
  const missing = readiness.filter((r) => !r.ok);
  const isReady = missing.length === 0;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && requestClose()}>
        <DialogContent className="max-w-[96vw] xl:max-w-[1400px] max-h-[95vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-5 pt-4 pb-3 border-b space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <span className="truncate max-w-full">
                    {mode === "create"
                      ? row.sourceLabel
                        ? `Duplicate of ${row.sourceLabel}`
                        : "New Class"
                      : draft?.title ?? row.title}
                  </span>
                  <Badge variant="outline" className={STATUS_META[draft?.visibility_status ?? "published"].badgeClass}>
                    <span
                      className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${
                        STATUS_META[draft?.visibility_status ?? "published"].dot
                      }`}
                    />
                    {STATUS_META[draft?.visibility_status ?? "published"].label}
                  </Badge>
                  {dirty && (
                    <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-900">
                      Unsaved
                    </Badge>
                  )}
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  {mode === "create"
                    ? "New draft. Fill in what you need — you can keep editing after Save."
                    : "Everything about this class in one place. Click Save when you're done."}
                </p>

              </div>
              {mode === "edit" && (
                <Button size="sm" variant="outline" onClick={previewAsParent} className="shrink-0">
                  <ExternalLink className="h-4 w-4 mr-1.5" /> Open public page
                </Button>
              )}
            </div>

            {/* Publish-readiness strip */}
            {draft && isFullEditor && (
              <div
                className={`rounded-md border px-3 py-2 text-sm ${
                  isReady
                    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                    : "border-amber-200 bg-amber-50 text-amber-900"
                }`}
              >
                <div className="flex items-center gap-2 font-medium">
                  {isReady ? (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      Ready to publish
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4" />
                      {missing.length} {missing.length === 1 ? "item" : "items"} still needed
                    </>
                  )}
                </div>
                {!isReady && (
                  <ul className="mt-1.5 space-y-0.5">
                    {missing.map((m) => (
                      <li key={m.key}>
                        <button
                          type="button"
                          onClick={() => setTab(m.section)}
                          className="text-left text-sm hover:underline inline-flex items-center gap-1.5"
                        >
                          <Circle className="h-3 w-3 opacity-60" />
                          {m.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </DialogHeader>

          {loading || !draft ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading class…
            </div>
          ) : (
            <div className="flex-1 min-h-0 flex overflow-hidden">
              {/* Editor pane (nav + form) */}
              <div className="flex-1 min-w-0 flex flex-col md:flex-row overflow-hidden">
                {/* Left nav */}
                <nav className="w-full md:w-52 md:shrink-0 md:border-r border-b md:border-b-0 bg-muted/30 md:py-3 py-1 overflow-x-auto md:overflow-y-auto flex md:block whitespace-nowrap">
                  {TABS.map(({ key, label, Icon }) => {
                    const has = errors[key]?.length ?? 0;
                    const disabled =
                      (!isFullEditor && key !== "basics" && key !== "images") ||
                      (mode === "create" && key === "images");
                    return (
                      <button
                        key={key}
                        onClick={() => setTab(key)}
                        disabled={disabled}
                        className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 border-l-2 transition-colors ${
                          tab === key
                            ? "border-primary bg-background text-foreground font-medium"
                            : "border-transparent text-muted-foreground hover:bg-background/60"
                        } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="flex-1">{label}</span>
                        {has > 0 && (
                          <span className="text-[10px] px-1.5 rounded-full bg-destructive text-destructive-foreground">
                            {has}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </nav>

                {/* Tab body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

                  {errors[tab]?.length ? (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Fix these before saving</AlertTitle>
                      <AlertDescription>
                        <ul className="list-disc pl-5 space-y-0.5 mt-1">
                          {errors[tab]!.map((m, i) => (
                            <li key={i}>{m}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  {tab === "basics" && (
                    <BasicsTab
                      draft={draft}
                      patch={patch}
                      readOnly={!isFullEditor}
                      onTitleChange={(v) => {
                        patch({ title: v });
                        if (mode === "create" && !slugTouchedRef.current) {
                          setSlugInput(slugify(v));
                        }
                      }}
                    />
                  )}
                  {tab === "schedule" && isFullEditor && (
                    <ScheduleTab draft={draft} patch={patch} dbClassId={row.dbId} />
                  )}
                  {tab === "registration" && isFullEditor && (
                    <RegistrationTab
                      draft={draft}
                      patch={patch}
                      dbClassId={row.dbId}
                      slug={slugInput || row.slug}
                    />
                  )}
                  {tab === "pricing" && isFullEditor && <PricingTab draft={draft} patch={patch} />}
                  {tab === "instructor" && isFullEditor && (
                    <InstructorTab
                      draft={draft}
                      patch={patch}
                      dbClassId={row.dbId}
                      slug={slugInput || row.slug}
                      onLiveCountChange={setLiveAssignmentCount}
                      disabledReason={mode === "create" ? "Save the draft first, then assign an instructor." : null}
                    />
                  )}
                  {tab === "images" && (
                    <ImagesTab
                      currentImageUrl={draft.thumbnail_url}
                      canUpload={!!row.dbId}
                      onSave={saveImage}
                    />
                  )}
                  {tab === "supplies" && isFullEditor && <SuppliesTab draft={draft} patch={patch} />}
                  {tab === "welcome_packet" && mode === "create" && (
                    <p className="text-sm text-muted-foreground">
                      Save the class first, then add its welcome packet.
                    </p>
                  )}
                  {/* Stays mounted so switching tabs never discards packet edits. */}
                  {mode !== "create" && (
                    <div className={tab === "welcome_packet" ? undefined : "hidden"}>
                      <WelcomePacketTab
                        classSlug={row.slug}
                        className={draft.title || row.title}
                        sessionLabels={(draft.sessions ?? [])
                          .map((s) => (s.label ?? "").trim())
                          .filter(Boolean)}
                        onDirtyChange={setPacketDirty}
                        registerSave={registerPacketSave}
                      />
                    </div>
                  )}

                  {tab === "history" && (
                    <ClassHistoryPanel classId={row.dbId ?? null} slug={row.slug} />
                  )}

                  {tab === "advanced" && (
                    <AdvancedTab
                      draft={draft}
                      patch={patch}
                      createMode={mode === "create"}
                      slugInput={slugInput}
                      currentSlug={row.slug}
                      onSlugChange={(v) => {
                        slugTouchedRef.current = true;
                        setSlugInput(v);
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Live parent preview (desktop) */}
              {previewItem && (
                <aside className="hidden lg:flex w-[420px] shrink-0 border-l">
                  <ClassPreviewPane classItem={previewItem} />
                </aside>
              )}
            </div>
          )}

          <DialogFooter className="px-6 py-3 border-t bg-muted/20">
            <div className="flex-1 text-xs text-muted-foreground">
              {dirty ? "You have unsaved changes." : "All changes saved."}
            </div>
            <Button variant="ghost" onClick={requestClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving || loading || !draft}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      <Dialog open={showLeaveWarn} onOpenChange={setShowLeaveWarn}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            You have edits that haven't been saved. Closing now will lose them.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowLeaveWarn(false)}>
              Keep editing
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowLeaveWarn(false);
                onClose();
              }}
            >
              Discard changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ---------------- Tabs ---------------- */

function BasicsTab({
  draft,
  patch,
  readOnly,
  createMode = false,
  slugInput = "",
  onSlugChange,
  onTitleChange,
}: {
  draft: Draft;
  patch: (p: Partial<Draft>) => void;
  readOnly: boolean;
  createMode?: boolean;
  slugInput?: string;
  onSlugChange?: (v: string) => void;
  onTitleChange?: (v: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label>Title</Label>
          <Input
            value={draft.title}
            onChange={(e) => (onTitleChange ? onTitleChange(e.target.value) : patch({ title: e.target.value }))}
            maxLength={200}
            disabled={readOnly}
          />
        </div>
        {/* URL slug moved to the Advanced section. */}
        <div>
          <Label>Category</Label>
          <Input
            value={draft.category}
            onChange={(e) => patch({ category: e.target.value })}
            maxLength={100}
            disabled={readOnly}
            placeholder="Fine Arts, STEM, Life Skills…"
          />
        </div>
        <div>
          <Label>Ages</Label>
          <Input
            value={draft.ages}
            onChange={(e) => patch({ ages: e.target.value })}
            maxLength={100}
            disabled={readOnly}
            placeholder="Ages 8–12"
          />
        </div>
      </div>
      <div>
        <Label>Short description</Label>
        <Input
          value={draft.short_description}
          onChange={(e) => patch({ short_description: e.target.value })}
          maxLength={300}
          disabled={readOnly}
          placeholder="One line families see in class lists"
        />
      </div>
      <div>
        <Label>Full description</Label>
        <Textarea
          rows={8}
          value={draft.description}
          onChange={(e) => patch({ description: e.target.value })}
          maxLength={5000}
          disabled={readOnly}
          placeholder="The story families see on the class page."
        />
        <p className="text-xs text-muted-foreground mt-1">{draft.description.length}/5000</p>
      </div>
      <div>
        <Label>Publishing status</Label>
        <Select
          value={draft.visibility_status}
          onValueChange={(v) => patch({ visibility_status: v as VisibilityStatus })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LIFECYCLE_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_META[s].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          {STATUS_META[draft.visibility_status].description}
        </p>
      </div>

      <FinancialSnapshotPanel
        input={{
          billingUnit: draft.billing_unit,
          pricePerChild: Number(draft.price_per_child) || 0,
          hasInstructor: draft.management_type === "external_provider" ? true : !!draft.instructor_id,
          externalPayment: draft.management_type === "external_provider",
          scheduleText: draft.schedule,
          classLengthText: draft.class_length,
          sessionLengthWeeks:
            (draft.duration || draft.class_length || "").match(/(\d+)\s*[-\s]?week/i)?.[1]
              ? parseInt((draft.duration || draft.class_length).match(/(\d+)\s*[-\s]?week/i)![1], 10)
              : null,
          // A semester session row with its own price is what checkout charges.
          semesterPriceOverride:
            draft.sessions.find((s) => /semester/i.test(s.label ?? ""))?.price ?? null,
          enrolled: 0,
          capacity: draft.capacity,
        }}
      />
    </div>
  );
}


function ScheduleTab({ draft, patch, dbClassId }: { draft: Draft; patch: (p: Partial<Draft>) => void; dbClassId?: string | null }) {
  const addSession = () =>
    patch({ sessions: [...draft.sessions, { label: `Session ${draft.sessions.length + 1}`, dates: "" }] });
  const updateSession = (i: number, up: Partial<SessionRow>) => {
    const next = draft.sessions.slice();
    next[i] = { ...next[i], ...up };
    patch({ sessions: next });
  };
  const removeSession = (i: number) => patch({ sessions: draft.sessions.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-4">
      <div>
        <Label>Schedule summary</Label>
        <Input
          value={draft.schedule}
          onChange={(e) => patch({ schedule: e.target.value })}
          placeholder="e.g. Saturdays 10:00 AM – Noon"
        />
        <p className="text-xs text-muted-foreground mt-1">
          One-line summary shown on class cards. Detailed dates come from sessions below.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Preferred days</Label>
          <Input value={draft.preferred_days} onChange={(e) => patch({ preferred_days: e.target.value })} />
        </div>
        <div>
          <Label>Preferred time</Label>
          <Input value={draft.preferred_time} onChange={(e) => patch({ preferred_time: e.target.value })} />
        </div>
        <div>
          <Label>Class length</Label>
          <Input value={draft.class_length} onChange={(e) => patch({ class_length: e.target.value })} placeholder="e.g. 90 minutes" />
        </div>
        <div>
          <Label>Duration</Label>
          <Input value={draft.duration} onChange={(e) => patch({ duration: e.target.value })} placeholder="e.g. 4 weeks" />
        </div>
        <div className="col-span-2">
          <Label>Start date (display)</Label>
          <Input value={draft.start_date} onChange={(e) => patch({ start_date: e.target.value })} placeholder="Optional — leave blank to derive from sessions" />
          <p className="text-xs text-muted-foreground mt-1">
            {draft.sessions.length > 0
              ? "Optional. Dates are generated from the sessions below when this is blank."
              : "Optional short label shown on the class card (e.g. \"Starts May 9, 2026\")."}
          </p>
        </div>
      </div>

      <div className="rounded-md border">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/40">
          <div>
            <div className="text-sm font-medium">Sessions</div>
            <div className="text-xs text-muted-foreground">Each entry becomes a bookable session on the class page.</div>
          </div>
          <Button size="sm" variant="outline" onClick={addSession}>
            <Plus className="h-4 w-4 mr-1" /> Add session
          </Button>
        </div>
        {draft.sessions.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">No sessions yet.</p>
        ) : (
          <div className="divide-y">
            {draft.sessions.map((s, i) => (
              <div key={i} className="p-3 grid grid-cols-12 gap-2 items-start">
                <div className="col-span-3">
                  <Label className="text-xs">Label</Label>
                  <Input value={s.label} onChange={(e) => updateSession(i, { label: e.target.value })} />
                </div>
                <div className="col-span-5">
                  <Label className="text-xs">Dates</Label>
                  <Input
                    value={s.dates}
                    onChange={(e) => updateSession(i, { dates: e.target.value })}
                    placeholder="Saturdays May 9 – 30, 10 AM – Noon"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Price override</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={s.price ?? ""}
                    onChange={(e) =>
                      updateSession(i, { price: e.target.value === "" ? null : Number(e.target.value) })
                    }
                    placeholder="—"
                  />
                </div>
                <div className="col-span-1">
                  <Label className="text-xs">Full</Label>
                  <div className="pt-2">
                    <Switch checked={!!s.full} onCheckedChange={(v) => updateSession(i, { full: v })} />
                  </div>
                </div>
                <div className="col-span-1">
                  <Label className="text-xs" title="Closed to new registration; existing families keep their seats">
                    Closed
                  </Label>
                  <div className="pt-2">
                    <Switch checked={!!s.closed} onCheckedChange={(v) => updateSession(i, { closed: v })} />
                  </div>
                </div>
                <div className="col-span-12 flex justify-end">
                  <Button size="icon" variant="ghost" onClick={() => removeSession(i)} aria-label="Remove session">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ScheduleOptionsSection dbClassId={dbClassId} />
    </div>
  );
}

function RegistrationTab({
  draft,
  patch,
  dbClassId,
  slug,
}: {
  draft: Draft;
  patch: (p: Partial<Draft>) => void;
  dbClassId?: string;
  slug?: string;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Capacity</Label>
          <Input
            type="number"
            min={1}
            value={draft.capacity}
            onChange={(e) => patch({ capacity: Math.max(1, Number(e.target.value) || 1) })}
          />
          <p className="text-xs text-muted-foreground mt-1">Total seats across all sessions.</p>
        </div>
        <div>
          <Label>Minimum enrollment</Label>
          <Input
            type="number"
            min={0}
            value={draft.min_enrollment}
            onChange={(e) => patch({ min_enrollment: Math.max(0, Number(e.target.value) || 0) })}
          />
          <p className="text-xs text-muted-foreground mt-1">Break-even count for the class to run.</p>
        </div>
      </div>
      <div className="space-y-3 rounded-md border p-3">
        <label className="flex items-start gap-3">
          <Switch
            checked={draft.accepts_registrations}
            onCheckedChange={(v) => patch({ accepts_registrations: v })}
          />
          <div>
            <div className="text-sm font-medium">Accept paid registrations</div>
            <p className="text-xs text-muted-foreground">
              When off, the Register button is disabled even if the class is Published.
            </p>
          </div>
        </label>
        <label className="flex items-start gap-3">
          <Switch
            checked={draft.registration_enabled}
            onCheckedChange={(v) => patch({ registration_enabled: v })}
          />
          <div>
            <div className="text-sm font-medium">Registration is open</div>
            <p className="text-xs text-muted-foreground">
              Turn this off to pause sign-ups without changing the class's publishing status.
            </p>
          </div>
        </label>
        <label className="flex items-start gap-3">
          <Switch checked={draft.calendar_visible} onCheckedChange={(v) => patch({ calendar_visible: v })} />
          <div>
            <div className="text-sm font-medium">Show on the public calendar</div>
            <p className="text-xs text-muted-foreground">
              Uncheck to keep a class visible on the Classes page but hide it from the calendar view.
            </p>
          </div>
        </label>
      </div>
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Registration windows are shared</AlertTitle>
        <AlertDescription>
          Semester/month tabs (Fall, August, etc.) are managed in Configuration → Registration Periods.
          They apply across every class.
        </AlertDescription>
      </Alert>
      <SemesterDatePreview schedule={draft.schedule} />
      <ClassRosterPanel
        dbClassId={dbClassId}
        slug={slug ?? ""}
        title={draft.title}
        capacity={draft.capacity}
        minEnrollment={draft.min_enrollment}
        registrationOpen={draft.registration_enabled && draft.accepts_registrations}
        sessions={(draft.sessions ?? []) as unknown as ClassSession[]}
        scheduleText={draft.schedule}
      />
    </div>
  );
}

function SemesterDatePreview({ schedule }: { schedule: string }) {
  const weekdays = parseWeekdays(schedule) as WeekdayName[];
  const fall = describeSemesterForClass("fall_2026", weekdays);
  const spring = describeSemesterForClass("spring_2027", weekdays);
  if (!fall && !spring) return null;
  return (
    <div className="rounded-md border bg-muted/30 p-3 space-y-2">
      <div className="text-sm font-medium">Academic period preview</div>
      <p className="text-xs text-muted-foreground">
        Based on this class's weekday(s). Semester dates are managed centrally in
        Configuration → Academic Calendar.
      </p>
      {fall && (
        <div className="text-sm">
          <div className="font-medium">{fall.label}</div>
          <ul className="text-muted-foreground text-xs pl-4 list-disc">
            {fall.lines.map((l) => (
              <li key={l.weekday}>{l.text} <span className="opacity-60">({l.weeks} weeks)</span></li>
            ))}
          </ul>
        </div>
      )}
      {spring && (
        <div className="text-sm">
          <div className="font-medium">{spring.label}</div>
          <ul className="text-muted-foreground text-xs pl-4 list-disc">
            {spring.lines.map((l) => (
              <li key={l.weekday}>{l.text} <span className="opacity-60">({l.weeks} weeks)</span></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PricingTab({ draft, patch }: { draft: Draft; patch: (p: Partial<Draft>) => void }) {
  const priceNumber = Number(draft.price_per_child);
  const isExternal = draft.management_type === "external_provider";
  const pricingInput = {
    pricePerChild: Number.isFinite(priceNumber) ? priceNumber : 0,
    billingUnit: draft.billing_unit,
    priceDisplay: draft.price_display,
    externalPayment: isExternal,
    displayOmitsUnitApproved: draft.price_display_omits_unit,
  };
  const auto = autoFormattedPrice(pricingInput);
  const overrideActive = draft.price_display.trim().length > 0;
  const overrideMismatch = overrideActive && !displayMatchesAuto(pricingInput);
  const bareMoney =
    Number.isFinite(priceNumber) && priceNumber > 0
      ? `$${Number.isInteger(priceNumber) ? priceNumber : priceNumber.toFixed(2)}`
      : "";
  const overrideIsBareMoney =
    overrideActive && bareMoney.length > 0 &&
    draft.price_display.trim().toLowerCase() === bareMoney.toLowerCase();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Price per child</Label>
          <Input
            type="number"
            step="0.01"
            value={draft.price_per_child}
            onChange={(e) => patch({ price_per_child: e.target.value })}
            placeholder="0.00"
          />
          <p className="text-xs text-muted-foreground mt-1">Leave blank for free classes.</p>
        </div>
        <div>
          <Label>Billing unit</Label>
          <Select
            value={draft.billing_unit}
            onValueChange={(v) => patch({ billing_unit: v as BillingUnit })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BILLING_UNITS.map((u) => (
                <SelectItem key={u} value={u}>
                  {billingUnitLabel(u).replace(/^./, (c) => c.toUpperCase())}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            {draft.billing_unit === "per_semester"
              ? "The price above is the total for the full semester."
              : draft.billing_unit === "per_month"
                ? "The price above is charged each month."
                : "The price above is charged per session."}
          </p>
        </div>
        <div className="col-span-2">
          <Label>Price display (optional)</Label>
          <Input
            value={draft.price_display}
            onChange={(e) => patch({ price_display: e.target.value })}
            placeholder={auto ? `e.g. "${auto}"` : 'e.g. "$60 / 4-week session"'}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Wording override for the public card. If blank, the site shows{" "}
            <strong>{auto || "—"}</strong> automatically.
          </p>
          {overrideActive && overrideIsBareMoney && (
            <label className="mt-2 flex items-start gap-2 rounded-md border border-border/60 bg-muted/40 p-2 text-xs">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={draft.price_display_omits_unit}
                onChange={(e) => patch({ price_display_omits_unit: e.target.checked })}
              />
              <span>
                <strong>Intentionally omit the billing unit</strong> on the public card.
                Turn this on if parents should see just <em>{draft.price_display}</em> instead of{" "}
                <em>{auto || "—"}</em>. Pricing Health treats this as resolved.
              </span>
            </label>
          )}
          {overrideMismatch && (
            <Alert className="mt-2">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Custom wording doesn't match the structured price</AlertTitle>
              <AlertDescription>
                The public card will read <strong>{draft.price_display}</strong>, but checkout
                and reporting always use the structured price above ({auto || "not set"}).
                {overrideIsBareMoney
                  ? " Tick the box above if this bare price is intentional."
                  : ""}
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Session-level overrides</AlertTitle>
        <AlertDescription>
          Individual sessions can override the price on the Schedule tab.
        </AlertDescription>
      </Alert>
    </div>
  );
}



function InstructorTab({
  draft,
  patch,
  dbClassId,
  slug,
  onLiveCountChange,
  disabledReason = null,
}: {
  draft: Draft;
  patch: (p: Partial<Draft>) => void;
  dbClassId?: string | null;
  slug: string;
  onLiveCountChange?: (count: number) => void;
  disabledReason?: string | null;
}) {
  if (disabledReason) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Assign after saving</AlertTitle>
        <AlertDescription>{disabledReason}</AlertDescription>
      </Alert>
    );
  }
  const isExternal = draft.management_type === "external_provider";
  return (
    <div className="space-y-4">
      <div className="rounded-md border p-4 space-y-3">
        <div className="text-sm font-medium">Class Management</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => patch({ management_type: "silver_sage" })}
            className={`rounded-md border px-3 py-2 text-left text-sm transition ${
              !isExternal ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-muted"
            }`}
          >
            <div className="font-medium">Silver Sage Managed</div>
            <div className="text-xs text-muted-foreground">
              We handle the instructor, registration, and payments.
            </div>
          </button>
          <button
            type="button"
            onClick={() => patch({ management_type: "external_provider" })}
            className={`rounded-md border px-3 py-2 text-left text-sm transition ${
              isExternal ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-muted"
            }`}
          >
            <div className="font-medium">Privately Managed by Provider</div>
            <div className="text-xs text-muted-foreground">
              An outside provider runs the class. We only host the listing.
            </div>
          </button>
        </div>
      </div>

      {isExternal ? (
        <div className="rounded-md border p-4 space-y-3">
          <div className="text-sm font-medium">Provider Details</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm space-y-1">
              <div className="text-xs text-muted-foreground">Provider name</div>
              <input
                className="w-full rounded-md border px-2 py-1.5 text-sm bg-background"
                value={draft.provider_name}
                onChange={(e) => patch({ provider_name: e.target.value })}
                placeholder="e.g. Team Alini"
              />
            </label>
            <label className="text-sm space-y-1">
              <div className="text-xs text-muted-foreground">External registration URL</div>
              <input
                className="w-full rounded-md border px-2 py-1.5 text-sm bg-background"
                value={draft.external_signup_url}
                onChange={(e) => patch({ external_signup_url: e.target.value })}
                placeholder="https://..."
              />
            </label>
            <label className="text-sm space-y-1">
              <div className="text-xs text-muted-foreground">Provider contact email</div>
              <input
                className="w-full rounded-md border px-2 py-1.5 text-sm bg-background"
                value={draft.provider_contact_email}
                onChange={(e) => patch({ provider_contact_email: e.target.value })}
                placeholder="contact@example.com"
              />
            </label>
            <label className="text-sm space-y-1">
              <div className="text-xs text-muted-foreground">Provider contact phone</div>
              <input
                className="w-full rounded-md border px-2 py-1.5 text-sm bg-background"
                value={draft.provider_contact_phone}
                onChange={(e) => patch({ provider_contact_phone: e.target.value })}
                placeholder="(555) 555-5555"
              />
            </label>
          </div>
          <label className="text-sm space-y-1 block">
            <div className="text-xs text-muted-foreground">Optional public contact text</div>
            <textarea
              className="w-full rounded-md border px-2 py-1.5 text-sm bg-background min-h-[70px]"
              value={draft.provider_public_contact_text}
              onChange={(e) => patch({ provider_public_contact_text: e.target.value })}
              placeholder="Registration and instruction are managed directly by the class provider."
            />
          </label>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Instructor details are managed by the provider.</AlertTitle>
            <AlertDescription>
              A Silver Sage primary instructor is not required. Silver Sage checkout is disabled — the
              public card will link out to the provider's registration URL.
            </AlertDescription>
          </Alert>
        </div>
      ) : (
        <ClassSessionInstructorPanel
          classId={dbClassId ?? null}
          slug={slug}
          legacyContactLabel={draft.instructor_id ? (draft.instructor_label ?? draft.instructor_id) : null}
          onLiveCountChange={onLiveCountChange}
        />
      )}
    </div>
  );
}

function ImagesTab({
  currentImageUrl,
  canUpload,
  onSave,
}: {
  currentImageUrl: string | null;
  canUpload: boolean;
  onSave: (file: File) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  if (!canUpload) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Image editing requires a database class</AlertTitle>
        <AlertDescription>
          This class's image is set in code. Migrate it to the database first, then upload here.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <MediaManager
      title="Class cover image"
      currentImageUrl={currentImageUrl}
      uploading={busy}
      onSave={async (file) => {
        setBusy(true);
        try {
          await onSave(file);
        } catch (e) {
          const stage = (e as { __operation?: string })?.__operation ?? "Save class image";
          friendlyError({
            what: "I couldn't save that image.",
            meaning: `The step that failed: ${stage}.`,
            changed: "Your current class image is unchanged.",
            nextStep:
              typeof navigator !== "undefined" && navigator.onLine === false
                ? "Your connection dropped. Reconnect and try again — your pending image is still selected."
                : "Try again in a moment, or open Show technical details for the exact reason.",
            technical: e,
            operation: stage,
          });
          throw e;
        } finally {
          setBusy(false);
        }
      }}
    />
  );
}

function SuppliesTab({ draft, patch }: { draft: Draft; patch: (p: Partial<Draft>) => void }) {
  const addTo = (kind: "required" | "recommended") => {
    const key = kind === "required" ? "supplies_required" : "supplies_recommended";
    patch({ [key]: [...draft[key], { name: "", url: "" }] } as Partial<Draft>);
  };
  const updateItem = (kind: "required" | "recommended", i: number, up: Partial<SupplyItem>) => {
    const key = kind === "required" ? "supplies_required" : "supplies_recommended";
    const list = draft[key].slice();
    list[i] = { ...list[i], ...up };
    patch({ [key]: list } as Partial<Draft>);
  };
  const removeItem = (kind: "required" | "recommended", i: number) => {
    const key = kind === "required" ? "supplies_required" : "supplies_recommended";
    patch({ [key]: draft[key].filter((_, idx) => idx !== i) } as Partial<Draft>);
  };

  const section = (kind: "required" | "recommended") => {
    const list = kind === "required" ? draft.supplies_required : draft.supplies_recommended;
    return (
      <div className="rounded-md border">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/40">
          <div className="text-sm font-medium capitalize">{kind} supplies</div>
          <Button size="sm" variant="outline" onClick={() => addTo(kind)}>
            <Plus className="h-4 w-4 mr-1" /> Add item
          </Button>
        </div>
        {list.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">Nothing added.</p>
        ) : (
          <div className="divide-y">
            {list.map((it, i) => (
              <div key={i} className="p-3 grid grid-cols-12 gap-2 items-start">
                <div className="col-span-5">
                  <Label className="text-xs">Name</Label>
                  <Input value={it.name} onChange={(e) => updateItem(kind, i, { name: e.target.value })} />
                </div>
                <div className="col-span-6">
                  <Label className="text-xs">Link (optional)</Label>
                  <Input
                    value={it.url}
                    onChange={(e) => updateItem(kind, i, { url: e.target.value })}
                    placeholder="https://…"
                  />
                </div>
                <div className="col-span-1 flex justify-end pt-5">
                  <Button size="icon" variant="ghost" onClick={() => removeItem(kind, i)} aria-label="Remove item">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {section("required")}
      {section("recommended")}
      <div>
        <Label>Notes for families</Label>
        <Textarea
          rows={3}
          value={draft.supplies_note}
          onChange={(e) => patch({ supplies_note: e.target.value })}
          maxLength={1000}
          placeholder="Bring water. Wear closed-toe shoes."
        />
      </div>
    </div>
  );
}

function AdvancedTab({
  draft,
  patch,
  createMode,
  slugInput,
  currentSlug,
  onSlugChange,
}: {
  draft: Draft;
  patch: (p: Partial<Draft>) => void;
  createMode: boolean;
  slugInput: string;
  currentSlug: string;
  onSlugChange: (v: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div className="rounded-md border p-4 space-y-3">
        <div className="text-sm font-medium">Public URL</div>
        {createMode ? (
          <>
            <Input
              value={slugInput}
              onChange={(e) => onSlugChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
              maxLength={80}
              placeholder="auto-generated from title"
            />
            <p className="text-xs text-muted-foreground">
              Families will visit <code>/classes?open={slugInput || "…"}</code>. This locks after the first save.
            </p>
          </>
        ) : (
          <>
            <Input value={currentSlug} readOnly disabled />
            <p className="text-xs text-muted-foreground">
              The public URL is set when the class is created and can't be changed here.
            </p>
          </>
        )}
      </div>

      <div className="rounded-md border p-4 space-y-3">
        <div>
          <div className="text-sm font-medium">Publishing status</div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Controls whether families can see and register. Also available at the top of the Overview section.
          </p>
        </div>
        <Select
          value={draft.visibility_status}
          onValueChange={(v) => patch({ visibility_status: v as VisibilityStatus })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LIFECYCLE_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_META[s].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{STATUS_META[draft.visibility_status].description}</p>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Danger zone</AlertTitle>
        <AlertDescription>
          To archive or delete a class, change its status to <strong>Archived</strong> here and Save.
          Archived classes disappear from the public catalog but keep their history.
        </AlertDescription>
      </Alert>
    </div>
  );
}

export default ClassEditorDialog;

